import axios, { type AxiosProgressEvent } from "axios";
import { saveAs } from "file-saver";

import { authAxios } from "@/lib/axios";

type ClipDownloadRequestOptions = {
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

type ResolveFilenameOptions = {
  contentDisposition?: string | null;
  fallbackName?: string | null;
  fallbackUrl?: string | null;
};

type DownloadClipVideoOptions = {
  clipId: string;
  downloadUrl?: string | null;
  fallbackName?: string | null;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

export function buildClipDownloadEndpoint(clipId: string): string {
  return `/api/clips/${encodeURIComponent(clipId)}/download`;
}

export function buildClipDownloadRequestConfig(options: ClipDownloadRequestOptions = {}) {
  return {
    responseType: "blob" as const,
    timeout: 0,
    onDownloadProgress: options.onDownloadProgress,
  };
}

function getExtensionFromUrl(url?: string | null): string {
  if (!url) return "mp4";

  try {
    const baseUrl = typeof window === "undefined" ? "http://localhost" : window.location.href;
    const pathname = new URL(url, baseUrl).pathname;
    return pathname.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase() || "mp4";
  } catch {
    return "mp4";
  }
}

function sanitizeDownloadName(name?: string | null): string {
  const safeName = (name || "clip").replace(/[^a-zA-Z0-9-_.]+/g, "_");
  return safeName || "clip";
}

function parseContentDispositionFilename(header?: string | null): string | null {
  if (!header) return null;

  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return encodedMatch[1];
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const bareMatch = header.match(/filename=([^;]+)/i);
  return bareMatch?.[1]?.trim() || null;
}

export function resolveClipDownloadFilename({
  contentDisposition,
  fallbackName,
  fallbackUrl,
}: ResolveFilenameOptions): string {
  const dispositionName = parseContentDispositionFilename(contentDisposition);
  if (dispositionName) return dispositionName;

  return `${sanitizeDownloadName(fallbackName)}.${getExtensionFromUrl(fallbackUrl)}`;
}

// Primary path: pull the bytes straight from R2 (public, CORS '*'). Uses the
// bare axios instance — NOT authAxios — so we neither prepend the API baseURL to
// the absolute R2 URL nor leak the JWT to the storage host.
async function downloadDirectFromR2(
  url: string,
  fileName: string,
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void,
): Promise<void> {
  const response = await axios.get(url, buildClipDownloadRequestConfig({ onDownloadProgress }));
  saveAs(response.data, fileName);
}

function triggerBrowserDownload(url: string, fileName: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// Fallback path: ask the API for a short-lived presigned R2 URL (with
// Content-Disposition baked in) and let the browser download it directly. The
// API signs a URL; it never streams the bytes.
async function downloadViaApiPresigned(clipId: string, fileName: string): Promise<void> {
  const response = await authAxios.get<{ url?: string }>(buildClipDownloadEndpoint(clipId));
  const url = response.data?.url;
  if (!url) {
    throw new Error("Presigned download URL missing from response");
  }
  triggerBrowserDownload(url, fileName);
}

export async function downloadPublicMedia(
  url: string,
  fallbackName?: string | null,
): Promise<void> {
  const fileName = resolveClipDownloadFilename({ fallbackName, fallbackUrl: url });
  try {
    await downloadDirectFromR2(url, fileName);
  } catch {
    triggerBrowserDownload(url, fileName);
  }
}

export async function downloadClipVideo({
  clipId,
  downloadUrl,
  fallbackName,
  onDownloadProgress,
}: DownloadClipVideoOptions): Promise<void> {
  const fileName = resolveClipDownloadFilename({ fallbackName, fallbackUrl: downloadUrl });

  if (downloadUrl) {
    try {
      await downloadDirectFromR2(downloadUrl, fileName, onDownloadProgress);
      return;
    } catch {
      // Direct R2 download failed — fall through to the presigned API fallback.
    }
  }

  await downloadViaApiPresigned(clipId, fileName);
}
